import { Component, OnInit, Input, OnDestroy, forwardRef, EventEmitter, Output, ViewChild, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { Subscription, BehaviorSubject } from "rxjs";
import { trigger, transition, animateChild, style, animate, query } from '@angular/animations';
import { RouterFilterReference } from "../../common/filters-router/filters-router.component";
import { FiltersTypeEnum } from 'world-archery-lib';
import { tap } from 'rxjs/operators';
import { IFilterComboComponent } from '../filters.enum';

@Component({
  selector: "lib-combo",
  templateUrl: "./combo.component.html",
  styleUrls: ["./combo.component.scss"],
  providers: [
    { provide: RouterFilterReference, useExisting: forwardRef(() => ComboComponent) }
  ],
  animations: [
    trigger('heightAnimation', [
      transition(':enter', [
        query('@*', animateChild(), { optional: true }),
        style({ height: 0, paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0, opacity: 0 }),
        animate('200ms', style({ height: '*', opacity: 1 }))
      ]),

      transition(':leave', [
        query('@*', animateChild(), { optional: true }),
        style({ height: '*', paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0, opacity: 1 }),
        animate('200ms', style({ height: 0, paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0, opacity: 0 })),

      ]),

      transition('* <=> *', [
        query('@*', animateChild(), { optional: true }),
        animate('200ms')
      ]),

    ]),
  ],
})
export class ComboComponent extends RouterFilterReference implements OnInit, OnDestroy {
  subscriptions: Array<Subscription> = [];

  /**
   * Element ref for combo of stencil.
   *
   * @type {ElementRef}
   * @memberof ComboComponent
   */
  @ViewChild("combo", {static:true}) combo: ElementRef;

  /**
   *
   * Name that filter must show before the combo
   * @type {string}
   * @memberof ComboComponent
   */
  @Input() name_filter: string;

  /**
   *
   * Subject that must pass the data to the component
   * @type {BehaviorSubject<Array<any>>}
   * @memberof ComboComponent
   */
  @Input() options_subject: BehaviorSubject<Array<any>>;

  /**
   *
   * Property name that must be access option object to change value of combo. Defaul : 'name'
   * @type {string}
   * @memberof ComboComponent
   */
  @Input() option_name_property: string = "name";

  /**
   * Input for change the function and update component.
   *
   * @memberof ComboComponent
   */
  @Input() hide_callback_function: BehaviorSubject<(value) =>  boolean>;

  /**
   * The full object of configuration.
   *
   * @type {IFilterComboComponent}
   * @memberof ComboComponent
   */
  @Input() allConfig: IFilterComboComponent;

  /**
   *
   * Options values getted by subject
   * @type {Array<any>}
   * @memberof ComboComponent
   */
  public options: Array<any> = [];

  /**
   *
   * Subject that send the content of combobox after selection has been made
   * @type {BehaviorSubject<any>}
   * @memberof ComboComponent
   */
  @Input() options_selected_subject: BehaviorSubject<any>;
  selected: any;

  /**
   *
   * id of html component
   * @type {string}
   * @memberof ComboComponent
   */
  @Input()
  id: string;

  /**
   * Set an specific initial state for combo. IF false initial state would be first element of array
   *
   * @type {boolean}
   * @memberof ComboComponent
   */
  @Input()
  initial_state: boolean = false;

  /**
   * A function callback for hide elements before they're show.
   *
   * @memberof ComboComponent
   */
  @Input()
  hideElementsFunction: (element: any) => boolean;

  constructor(private chg:ChangeDetectorRef) {
    super(FiltersTypeEnum.ComboBox);
  }
  ngOnInit() {
    if(this.hide_callback_function){
      const callBackSub = this.hide_callback_function.asObservable().pipe(
        tap(a=>{
            this.hideElementsFunction = a;
            this.chg.detectChanges();
        })
      ).subscribe();
      this.subscriptions.push(callBackSub);
    }
    const subject_listen_change_options = this.options_subject
      .asObservable()
      .subscribe(next => {
        if (next instanceof Array) {
          if (this.hideElementsFunction != undefined) {
            next = next.filter(this.hideElementsFunction);
          }
          this.options = next;
          if (this.initial_state === false) {
            if(this.allConfig.selectMultiple){
              this.options_selected_subject.next([next[0]]);
            }else{
              this.options_selected_subject.next(next[0]);
            }
          }
        } else {
          throw new Error("Select items must be array instance");
        }
      });
    const subject_out_listen_change = this.options_selected_subject
      .asObservable()
      .subscribe(next => {
        this.selected = next;
        this.changeData.emit(next);

      });
    this.subscriptions.push(
      subject_listen_change_options,
      subject_out_listen_change
    );
    this.combo.nativeElement.addEventListener('selectedData', (event) => this.onChange(event.detail));
    if(this.allConfig.renderFunc){
      this.combo.nativeElement['renderFunc'] = this.allConfig.renderFunc;
    }
    if(this.allConfig.renderFuncSelected){
      this.combo.nativeElement['renderFuncSelected'] = this.allConfig.renderFuncSelected;
    }
  }
  onChange(option) {
    this.options_selected_subject.next(option);
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach(value =>
      value ? value.unsubscribe() : undefined
    );
  }

  /**
   * A function for decide if the data should be hide or not and render combo.
   *
   * @param {Array<any>} data
   * @returns
   * @memberof ComboComponent
   */
  filterOptionsData(data: Array<any>) {
    if (this.hideElementsFunction != undefined) {
      return data.filter(this.hideElementsFunction);
    } else {
      return data;
    }
  }

  /**
   * Method for clear the combo.
   *
   * @memberof ComboComponent
   */
  clearCombo(){
    this.options_selected_subject.next([]);
  }
}
